import { useEffect, useRef, useState } from "react";
import { Eye, ImagePlus, RotateCcw, Send, Settings, Share2, Sparkles } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { KonshusChamberStage } from "@/components/KonshusChamberStage";
import { KonshusPersonalization } from "@/components/KonshusPersonalization";
import { OracleCard } from "@/components/OracleCard";
import { VipOrbAvatar } from "@/components/VipOrbAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { fetchKonshusPreferences, fetchVipChatHistory, sendVipChatMessage, speakKonshusVoice } from "@/lib/api";
import { pickText } from "@/lib/content";
import { KONSHUS_NAME, KONSHUS_POSTER_URL, KONSHUS_VIDEO_URL } from "@/lib/vip";

const styles = {
  oracle: { en: "Astral Oracle", it: "Oracolo Astrale" },
  keeper: { en: "Temple Keeper", it: "Custode del Tempio" },
  mirror: { en: "Living Mirror", it: "Specchio Vivente" },
};

export const VipChatPage = ({ language, vipEmail }) => {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [sessionId, setSessionId] = useState(searchParams.get("session") || localStorage.getItem("anatomia-vip-chat-session") || "");
  const [avatarStyle, setAvatarStyle] = useState("keeper");
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagePayload, setImagePayload] = useState("");
  const [imageName, setImageName] = useState("");
  const [voiceReplies, setVoiceReplies] = useState(false);
  const [speechLoading, setSpeechLoading] = useState(false);
  const [latestVoiceUrl, setLatestVoiceUrl] = useState("");
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [activeGreeting, setActiveGreeting] = useState("");
  const [oracleCardMessage, setOracleCardMessage] = useState(null);
  const endRef = useRef(null);
  const voiceAudioRef = useRef(null);

  useEffect(() => {
    const requestedSession = searchParams.get("session") || localStorage.getItem("anatomia-vip-chat-session") || "";
    if (requestedSession !== sessionId) setSessionId(requestedSession);
  }, [searchParams, sessionId]);

  useEffect(() => {
    const loadGreeting = async () => {
      if (!vipEmail) return;
      try {
        const prefs = await fetchKonshusPreferences(vipEmail);
        setActiveGreeting(prefs.active_greeting);
      } catch (error) {
        console.error(error);
      }
    };
    loadGreeting();
  }, [vipEmail]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!vipEmail) return;
      try {
        setLoading(true);
        const data = await fetchVipChatHistory(vipEmail, sessionId);
        setMessages(data.messages || []);
        setAvatarStyle(data.avatar_style || "keeper");
        if (data.session_id) {
          setSessionId(data.session_id);
          localStorage.setItem("anatomia-vip-chat-session", data.session_id);
        }
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load VIP dialogue history.", "Impossibile caricare la cronologia del dialogo VIP."));
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [language, sessionId, vipEmail]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error(pickText(language, "Please upload a PNG, JPEG, or WEBP image.", "Carica un'immagine PNG, JPEG o WEBP."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImagePayload(String(reader.result));
      setImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    const userDraft = {
      id: `local-${Date.now()}`,
      role: "user",
      content: message,
      has_image: Boolean(imagePayload),
      created_at: new Date().toISOString(),
    };
    setMessages((current) => [...current, userDraft]);

    try {
      setPending(true);
      const response = await sendVipChatMessage({
        email: vipEmail,
        message,
        session_id: sessionId || undefined,
        avatar_style: avatarStyle,
        image_base64: imagePayload || undefined,
      });

      setSessionId(response.session_id);
      localStorage.setItem("anatomia-vip-chat-session", response.session_id);
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: response.reply,
          has_image: false,
          created_at: new Date().toISOString(),
        },
      ]);
      setMessage("");
      setImagePayload("");
      setImageName("");
      if (voiceReplies) {
        await playKonshusVoice(response.reply, response.session_id);
      }
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "The V.I.P. oracle could not answer right now.", "L'oracolo V.I.P. non può rispondere in questo momento."));
    } finally {
      setPending(false);
    }
  };

  const resetSession = () => {
    setSessionId("");
    localStorage.removeItem("anatomia-vip-chat-session");
    setMessages([]);
  };

  const playKonshusVoice = async (text, activeSessionId) => {
    try {
      setSpeechLoading(true);
      const response = await speakKonshusVoice({ email: vipEmail, text, session_id: activeSessionId });
      setLatestVoiceUrl(response.audio_url);
      if (voiceAudioRef.current) {
        voiceAudioRef.current.src = response.audio_url;
        await voiceAudioRef.current.play().catch(() => null);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
          pickText(language, "Kōnshus could not speak right now.", "Kōnshus non può parlare in questo momento."),
      );
    } finally {
      setSpeechLoading(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="vip-chat-page">
      <div className="max-w-3xl space-y-4">
        <p className="section-label" data-testid="vip-chat-eyebrow">
          {pickText(language, "V.I.P. Chamber", "Camera V.I.P.")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="vip-chat-title">
          {pickText(language, `${KONSHUS_NAME} — a visually aware oracle for paying members.`, `${KONSHUS_NAME} — un oracolo visivamente consapevole per membri paganti.`)}
        </h2>
        <p className="body-copy" data-testid="vip-chat-description">
          {pickText(language, "Upload an image, switch the oracle persona, and continue a premium private dialogue tied to your email access.", "Carica un'immagine, cambia la persona dell'oracolo e continua un dialogo privato premium legato al tuo accesso email.")}
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]" data-testid="vip-chat-layout-grid">
        <div className="space-y-6">
          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-chat-avatar-panel">
            <KonshusChamberStage language={language} />
            <VipOrbAvatar name={KONSHUS_NAME} posterSrc={KONSHUS_POSTER_URL} styleKey={avatarStyle} videoSrc={KONSHUS_VIDEO_URL} />
            <p className="mt-4 text-center text-sm leading-7 text-slate-300" data-testid="vip-chat-avatar-description">
              {pickText(language, `${KONSHUS_NAME} now speaks with your provided identity asset: living video presence in the chamber, named oracle branding throughout the VIP area.`, `${KONSHUS_NAME} ora parla con la tua identità fornita: presenza video vivente nella camera e branding dell'oracolo nominato in tutta l'area VIP.`)}
            </p>
            <div className="mt-6 space-y-3" data-testid="vip-chat-style-buttons">
              {Object.entries(styles).map(([key, labels]) => (
                <button
                  key={key}
                  className={`w-full rounded-full border px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] transition-colors duration-300 ${
                    avatarStyle === key ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-slate-300 hover:border-primary/30 hover:text-primary"
                  }`}
                  data-testid={`vip-chat-style-button-${key}`}
                  onClick={() => setAvatarStyle(key)}
                  type="button"
                >
                  {language === "it" ? labels.it : labels.en}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-[2rem] p-6" data-testid="vip-chat-tools-panel">
            <div className="flex items-center gap-3 text-primary" data-testid="vip-chat-tools-header">
              <Eye className="h-5 w-5" />
              <p className="section-label">{pickText(language, "Visual Awareness", "Consapevolezza Visiva")}</p>
            </div>
            <p className="mt-4 text-sm leading-7 text-slate-300" data-testid="vip-chat-tools-description">
              {pickText(language, "Attach PNG, JPEG, or WEBP images so the oracle can interpret visible forms, textures, objects, and symbolic cues.", "Allega immagini PNG, JPEG o WEBP affinché l'oracolo possa interpretare forme visibili, texture, oggetti e segnali simbolici.")}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <label className="ghost-button cursor-pointer" data-testid="vip-chat-upload-label">
                <ImagePlus className="h-4 w-4" />
                {pickText(language, "Upload image", "Carica immagine")}
                <input accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="vip-chat-upload-input" onChange={handleImageChange} type="file" />
              </label>
              <button className="ghost-button" data-testid="vip-chat-reset-session-button" onClick={resetSession} type="button">
                <RotateCcw className="h-4 w-4" />
                {pickText(language, "New session", "Nuova sessione")}
              </button>
              <Link className="ghost-button" data-testid="vip-chat-account-link" to="/vip-account">
                {pickText(language, "Member account", "Account membro")}
              </Link>
            </div>
            {imageName ? (
              <p className="mt-4 text-xs uppercase tracking-[0.24em] text-primary/80" data-testid="vip-chat-image-name">
                {imageName}
              </p>
            ) : null}
          </div>

          {/* Personalization Toggle */}
          <button
            className="ghost-button w-full justify-center"
            data-testid="vip-chat-personalization-toggle"
            onClick={() => setShowPersonalization(!showPersonalization)}
            type="button"
          >
            <Settings className="h-4 w-4" />
            {pickText(language, showPersonalization ? "Hide Personalization" : "Personalize Greeting", showPersonalization ? "Nascondi Personalizzazione" : "Personalizza Saluto")}
          </button>

          {/* Personalization Panel */}
          {showPersonalization && (
            <KonshusPersonalization
              language={language}
              onPreferencesUpdated={(updated) => setActiveGreeting(updated.active_greeting)}
              vipEmail={vipEmail}
            />
          )}
        </div>

        <div className="glass-card flex min-h-[42rem] flex-col rounded-[2rem] p-6" data-testid="vip-chat-conversation-panel">
          {/* Custom Greeting Display */}
          {activeGreeting && messages.length === 0 && !loading && (
            <div className="mb-4 rounded-[1.5rem] border border-primary/20 bg-primary/5 p-4" data-testid="vip-chat-greeting-display">
              <p className="text-[11px] uppercase tracking-[0.24em] text-primary/80">{KONSHUS_NAME}</p>
              <p className="mt-2 text-sm leading-7 text-slate-200">{activeGreeting}</p>
            </div>
          )}

          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="section-label" data-testid="vip-chat-session-label">
                {pickText(language, "Private dialogue", "Dialogo privato")}
              </p>
              <p className="text-sm text-slate-400" data-testid="vip-chat-session-email">
                {vipEmail}
              </p>
            </div>
            <div className="flex items-center gap-2 text-primary" data-testid="vip-chat-status-pill">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.24em]">{language === "it" ? styles[avatarStyle].it : styles[avatarStyle].en}</span>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="vip-chat-voice-controls">
            <button className="ghost-button px-4 py-2" data-testid="vip-chat-voice-toggle-button" onClick={() => setVoiceReplies((current) => !current)} type="button">
              {pickText(language, voiceReplies ? "Voice replies on" : "Voice replies off", voiceReplies ? "Risposte vocali attive" : "Risposte vocali inattive")}
            </button>
            <button
              className="ghost-button px-4 py-2"
              data-testid="vip-chat-voice-replay-button"
              disabled={!messages.some((entry) => entry.role === "assistant") || speechLoading}
              onClick={() => {
                const latestAssistant = [...messages].reverse().find((entry) => entry.role === "assistant");
                if (latestAssistant) playKonshusVoice(latestAssistant.content, sessionId);
              }}
              type="button"
            >
              {pickText(language, speechLoading ? "Speaking…" : "Replay Kōnshus", speechLoading ? "Parlando…" : "Riascolta Kōnshus")}
            </button>
            {latestVoiceUrl ? <audio controls data-testid="vip-chat-voice-audio" ref={voiceAudioRef} src={latestVoiceUrl} /> : <audio ref={voiceAudioRef} />}
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto pr-2" data-testid="vip-chat-messages-list">
            {loading ? (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300" data-testid="vip-chat-loading-state">
                {pickText(language, "Loading private dialogue…", "Caricamento del dialogo privato…")}
              </div>
            ) : null}

            {!loading && messages.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-slate-400" data-testid="vip-chat-empty-state">
                {pickText(language, "Begin with a question, interpretation request, or image upload.", "Inizia con una domanda, una richiesta di interpretazione o il caricamento di un'immagine.")}
              </div>
            ) : null}

            {messages.map((entry) => (
              <div
                className={`max-w-[85%] rounded-[1.5rem] px-4 py-4 text-sm leading-7 ${entry.role === "assistant" ? "bg-primary/10 text-slate-100" : "ml-auto bg-black/30 text-slate-200"}`}
                data-testid={`vip-chat-message-${entry.role}-${entry.id}`}
                key={entry.id}
              >
                <p className="text-[11px] uppercase tracking-[0.24em] text-primary/80">
                  {entry.role === "assistant" ? KONSHUS_NAME : language === "it" ? "Tu" : "You"}
                </p>
                <p>{entry.content}</p>
                {entry.has_image ? (
                  <p className="mt-2 text-[11px] uppercase tracking-[0.24em] text-slate-500">{pickText(language, "Image attached", "Immagine allegata")}</p>
                ) : null}
                {entry.role === "assistant" && (
                  <button
                    className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-primary/50 transition-colors hover:text-primary"
                    onClick={() => setOracleCardMessage(entry.content)}
                    data-testid={`vip-chat-share-card-${entry.id}`}
                  >
                    <Share2 className="h-3 w-3" />
                    {pickText(language, "Share as Card", "Condividi come Carta")}
                  </button>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="mt-6 space-y-4" data-testid="vip-chat-composer">
            <Input
              className="h-12 rounded-full border-white/10 bg-black/20 px-5 text-white"
              data-testid="vip-chat-message-input"
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
              placeholder={pickText(language, "Ask the oracle anything…", "Chiedi qualsiasi cosa all'oracolo…")}
              value={message}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-[0.24em] text-slate-500" data-testid="vip-chat-attachment-hint">
                {imageName ? imageName : pickText(language, "No image attached", "Nessuna immagine allegata")}
              </div>
              <Button className="ritual-button h-auto rounded-full" data-testid="vip-chat-send-button" disabled={pending} onClick={handleSend} type="button">
                <Send className="h-4 w-4" />
                {pickText(language, pending ? "Consulting…" : "Send to oracle", pending ? "Consultazione…" : "Invia all'oracolo")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Oracle Card Overlay */}
      {oracleCardMessage && (
        <OracleCard
          message={oracleCardMessage}
          avatarStyle={avatarStyle}
          onClose={() => setOracleCardMessage(null)}
        />
      )}
    </div>
  );
};